import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Eye, EyeOff } from "lucide-react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import { getPendingTest, clearPendingTest } from "../../utils/pendingTestStorage";

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [loginSuccess, setLoginSuccess] = useState(false);

  // user added so we can check userType
  const { login, googleSignIn, user } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);

  // Handle redirect after login when user object becomes available
  useEffect(() => {
    const handlePostLoginRedirect = async () => {
      if (!loginSuccess || !user) return;

      // Only handle jobseeker redirect logic
      if (user.userType !== "jobseeker") {
        navigate("/");
        return;
      }

      // Check for pending test
      const pendingTest = getPendingTest();

      if (pendingTest && pendingTest.bypassVerification) {
        // HR-shared test - bypass verification and go directly to test
        clearPendingTest();
        navigate(`/job-seeker/take-test/${pendingTest.testId}?source=hr-shared`);
      } else if (pendingTest) {
        // Legacy pending test without bypass flag - check verification status
        try {
          const profileDoc = await getDoc(doc(db, "userProfiles", user.id));
          const profileData = profileDoc.exists() ? profileDoc.data() : null;

          const hasResume = profileData?.resumeUploaded === true;
          const isVerified = profileData?.skillsVerified === true;

          if (isVerified) {
            // User is verified - go directly to the pending test
            clearPendingTest();
            navigate(`/job-seeker/take-test/${pendingTest.testId}`);
          } else if (hasResume) {
            // Has resume but not verified - go to skills validation
            navigate("/job-seeker/skills-validation");
          } else {
            // No resume - go to resume upload
            navigate("/job-seeker/resume-upload");
          }
        } catch (error) {
          navigate("/job-seeker/resume-upload");
        }
      } else {
        // No pending test - normal flow to dashboard
        navigate("/job-seeker/dashboard");
      }
    };

    handlePostLoginRedirect();
  }, [loginSuccess, user, navigate, db]);

  const redirectAfterLogin = () => {
    // Set login success flag to trigger the useEffect
    setLoginSuccess(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const success = await login(formData.email, formData.password);
      if (success) {
        redirectAfterLogin();
      } else {
        setError("Invalid email or password. Please try again.");
      }
    } catch (err) {
      setError("Invalid credentials. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError("");
    try {
      const success = await googleSignIn();
      if (success) {
        redirectAfterLogin();
      } else {
        setError("Google sign-in failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animated Background Gradient with Floating Orbs */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50"></div>
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-purple-400/20 to-pink-600/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          {/* Logo with Gradient Border */}
          <div className="flex justify-center mb-6">
            <div className="relative p-1 rounded-glass bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500">
              <div className="bg-white rounded-glass p-3 flex items-center space-x-3">
                <img
                  src="/vetriaAI.png"
                  alt="VetriaAI Logo"
                  className="w-10 h-10"
                />
                <span className="text-2xl font-bold text-gradient">VetriaAI</span>
              </div>
            </div>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-2">Welcome back</h2>
          <p className="text-lg text-gray-600">Sign in to continue your journey</p>
        </div>

        {/* Glass Form Container */}
        <div className="glass-card backdrop-blur-glass-strong space-y-6">
          {/* Google Sign-In Button */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="btn-secondary w-full"
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              className="w-5 h-5"
            />
            <span>Continue with Google</span>
          </button>

          {/* Gradient Divider */}
          <div className="relative py-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 text-sm text-gray-500 bg-white/80 backdrop-blur-sm rounded-pill">
                or continue with email
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message with Glass Effect */}
            {error && (
              <div className="chip bg-red-50/80 border-red-300/50 text-red-700 backdrop-blur-sm px-4 py-3 rounded-button">
                {error}
              </div>
            )}

            {/* Email Input */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="input-glass w-full"
                placeholder="Enter your email"
              />
            </div>

            {/* Password Input */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="input-glass w-full pr-10"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-gray-700 font-medium"
              >
                Remember me
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                "Sign in"
              )}
            </button>

            <p className="text-center text-sm text-gray-600">
              Don't have an account?{" "}
              <Link to="/signup" className="font-semibold text-purple-600 hover:text-purple-800 transition-colors">
                Sign up here
              </Link>
            </p>
          </form>

        </div>
      </div>
    </div>
  );
};

export default LoginPage;
